import { useNavigate } from '@tanstack/react-router'
import { Package, Clock, CheckCircle, XCircle, Truck, Store, MapPin, CreditCard, ChevronRight } from 'lucide-react'
import type { OrderSummary, OrderStatus } from '../api/use-orders'
import { cn } from '@/shared/lib/utils'
import { formatPrice } from '@/shared/lib/format'

const STATUS_CONFIG: Record<OrderStatus, {
  label: string
  icon: typeof Clock
  color: string
  bgColor: string
}> = {
  pending_payment: { label: 'Ожидает оплаты', icon: CreditCard, color: 'text-orange-600 dark:text-orange-400', bgColor: 'bg-orange-50 dark:bg-orange-900/20' },
  new:             { label: 'Принят',          icon: Clock,      color: 'text-amber-600 dark:text-amber-400',  bgColor: 'bg-amber-50 dark:bg-amber-900/20' },
  confirmed:       { label: 'Подтверждён',     icon: CheckCircle, color: 'text-blue-600 dark:text-blue-400',   bgColor: 'bg-blue-50 dark:bg-blue-900/20' },
  in_progress:     { label: 'Собирается',      icon: Package,    color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-50 dark:bg-purple-900/20' },
  ready:           { label: 'Готов к выдаче',   icon: Package,    color: 'text-green-600 dark:text-green-400',  bgColor: 'bg-green-50 dark:bg-green-900/20' },
  completed:       { label: 'Завершён',         icon: CheckCircle, color: 'text-gray-500',                     bgColor: 'bg-gray-50 dark:bg-gray-800' },
  cancelled:       { label: 'Отменён',          icon: XCircle,    color: 'text-red-500',                       bgColor: 'bg-red-50 dark:bg-red-900/20' },
}

function getItemsText(count: number): string {
  if (count === 1) return '1 товар'
  if (count >= 2 && count <= 4) return `${count} товара`
  return `${count} товаров`
}

function formatOrderDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getDeliveryInfo(deliveryType: string): { icon: typeof Truck; label: string } {
  switch (deliveryType) {
    case 'cdek_pvz':  return { icon: MapPin, label: 'СДЭК ПВЗ' }
    case 'cdek_door': return { icon: Truck, label: 'СДЭК Курьер' }
    default:          return { icon: Store, label: 'Самовывоз' }
  }
}

export function OrderCard({ order }: { order: OrderSummary }) {
  const navigate = useNavigate()

  const status = STATUS_CONFIG[order.status] || STATUS_CONFIG.new
  const StatusIcon = status.icon
  const delivery = getDeliveryInfo(order.deliveryType)
  const DeliveryIcon = delivery.icon

  return (
    <div
      className="bg-white dark:bg-dark-card rounded-xl overflow-hidden cursor-pointer active:scale-[0.98] transition-all border border-silk/50 dark:border-dark-border"
      onClick={() => navigate({ to: '/order/$orderId', params: { orderId: order.id } })}
    >
      {/* Верхняя часть: номер + статус */}
      <div className="px-4 pt-4 pb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-bold text-[15px] text-ink dark:text-white">
            #{order.id.slice(0, 8)}
          </h3>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {formatOrderDate(order.createdAt)}
          </p>
        </div>
        <div className={cn('flex items-center gap-1.5 shrink-0 px-2.5 py-1 rounded-full text-[11px] font-semibold', status.bgColor, status.color)}>
          <StatusIcon className="w-3.5 h-3.5" />
          {status.label}
        </div>
      </div>

      {/* Нижняя часть: детали */}
      <div className="px-4 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3 text-[12px] text-muted-foreground">
          <span>{getItemsText(order.itemsCount)}</span>
          <span className="w-px h-3 bg-border" />
          <span className="flex items-center gap-1">
            <DeliveryIcon className="w-3.5 h-3.5" />
            {delivery.label}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="font-bold text-[15px] text-ink dark:text-white">
            {formatPrice(order.totalAmount)}
          </span>
          <ChevronRight className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
    </div>
  )
}
